import { createFileRoute } from '@tanstack/react-router'
import { Layout } from '@/components/custom/layout'
import { getUser } from '@/utils/auth'
import { reportCallHistory, exportCallHistory } from '@/api/report'
import { useQuery } from '@tanstack/react-query'
import {
  format,
  subYears,
  subMonths,
  subWeeks,
  subHours,
  subMinutes,
  startOfDay,
  endOfDay,
} from 'date-fns'
import { formatSecondToHour } from '@/utils/report'
import { callTypeView } from '@/components/reports-route/call-history/call-history-detail-table'
import { CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { tabFilterDate } from '@/constants/reports'
import { useReportCallHistoryFilterStore } from '@/store/report-call-history-filter-store'
import CallHistoryDetailFilterForm from '@/components/reports-route/call-history/call-history-detail-filter'
import CallHistoryDetailTable from '@/components/reports-route/call-history/call-history-detail-table'
import CallHistoryDetailTablePagination from '@/components/reports-route/call-history/call-history-detail-table-pagination'
import { useAllAgent, useAllQueues } from '@/hooks/use-setting-common'
import { Play } from 'lucide-react'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { useMutation } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import { Card, CardContent } from '@/components/ui/card'
import { throwException } from '@/exceptions/throw-exception.js'
import { getAllCampaigns } from '@/api/campaign'
import { isRoleAgent, isRoleTeamLeader } from '@/utils/auth'
import QUERY_KEY from '@/constants/use-query-key.js'
import { toast } from 'sonner'

export const Route = createFileRoute('/_auth/switchboard/call-history')({
  component: CallHistory,
})

function CallHistory() {
  const queryDataAllQueues = useAllQueues()
  const queryDataAllAgent = useAllAgent()

  const {
    from_date,
    to_date,
    setFromDate,
    setToDate,
    tabFilterDateDf,
    setTabFilterDateDf,
    filterState,
    timeRefresh,
    setTimeRefresh,
  } = useReportCallHistoryFilterStore(
    useShallow((state) => ({
      from_date: state.from_date,
      to_date: state.to_date,
      setFromDate: state.setFromDate,
      setToDate: state.setToDate,
      timeRefresh: state.timeRefresh,
      filterState: state.filterState,
      setTabFilterDateDf: state.setTabFilterDateDf,
      setTimeRefresh: state.setTimeRefresh,
      tabFilterDateDf: state.tabFilterDateDf,
    })),
  )
  const user = getUser()

  const fetchData = async () => {
    const payload = {
      callId: filterState.callId,
      callType: filterState.callType,
      called:
        filterState.calleds.length > 0
          ? filterState.calleds.map((e) => e.value).join(',')
          : '',
      caller: filterState.caller,
      campaignId:
        filterState.campaigns.length > 0
          ? filterState.campaigns.map((e) => e.value).join(',')
          : '',
      duration: filterState.duration,
      from: from_date ? format(from_date, 'yyyy-MM-dd HH:mm') : '',
      queueId:
        filterState.queues.length > 0
          ? filterState.queues.map((e) => e.value).join(',')
          : '',
      to: to_date ? format(to_date, 'yyyy-MM-dd HH:mm') : '',
      acdTimeGt: filterState.acdTimeGt || '',
      acdTimeLt: filterState.acdTimeLt || '',
    }

    if (isRoleAgent()) {
      payload.agentId = user.agent_id
    }

    if (isRoleTeamLeader()) {
      payload.groupId = [
        ...queryDataAllAgent
          .filter((e) => e.group_id == user?.group_id && e.agent_id)
          .map((a) => a.agent_id),
      ]
    }

    if (filterState.agents && filterState.agents.length > 0) {
      payload.groupId = filterState.agents.map((e) => e.agent_id)
    }

    try {
      const res = await reportCallHistory({
        tenantId: user?.tenant_id,
        data: payload,
        params: {
          page: filterState.page,
          pageSize: filterState.pageSize,
        },
      })

      const uniqueCampaigns = Object.values(
        res.data.data.reduce((acc, item) => {
          if (item.campaignId && !acc[item.campaignId]) {
            acc[item.campaignId] = item
          }
          return acc
        }, {}),
      )
        .filter((e) => !['HAPPYCALL', 'ZALO'].includes(e.campaignId))
        .map((item) => item.campaignId)

      let campaigns = []
      if (uniqueCampaigns.length > 0) {
        campaigns = await getAllCampaigns({
          campaignIds: uniqueCampaigns.join(','),
        })
      }

      return {
        data: res.data,
        campaigns: campaigns?.data?.list || [],
      }
    } catch (error) {
      throwException(error)
      return {}
    }
  }

  const { isLoading: isLoadingCallHistory, data: queryDataCallHistory } =
    useQuery({
      queryKey: [
        QUERY_KEY.GET_CALL_HISTORY_REPORT,
        {
          params: {
            page: filterState.page,
            pageSize: filterState.pageSize,
            timeRefresh,
          },
        },
      ],
      queryFn: fetchData,
      enabled: queryDataAllAgent.length > 0,
    })
  const dataCallHistory = getDataCallHistory({
    campaigns: queryDataCallHistory?.campaigns,
    queryDataCallHistory: queryDataCallHistory?.data,
    queryDataAllQueues,
    queryDataAllAgent,
  })

  const handleTabChange = (value) => {
    setTabFilterDateDf(value)
    if (!value) {
      setTabFilterDateDf('-1')
      return
    }
    switch (value) {
      case 'year':
        setFromDate(subYears(new Date(), 1))
        setToDate(new Date())
        break
      case 'month':
        setFromDate(subMonths(new Date(), 1))
        setToDate(new Date())

        break
      case 'week':
        setFromDate(subWeeks(new Date(), 1))
        setToDate(new Date())

        break
      case 'day':
        setFromDate(startOfDay(new Date()))
        setToDate(endOfDay(new Date()))

        break
      case 'hour':
        setFromDate(subHours(new Date(), 1))
        setToDate(new Date())

        break
      case 'half_hour':
        setFromDate(subMinutes(new Date(), 30))
        setToDate(new Date())

        break
      default:
    }

    setTimeRefresh(new Date())
  }

  const handleExportCallHistory = async () => {
    const payload = {
      username: '',
      agentId:
        filterState.agents.length > 0
          ? filterState.agents.map((e) => e.agent_id).join(',')
          : '',
      callId: filterState.callId,
      callType: filterState.callType,
      called:
        filterState.calleds.length > 0
          ? filterState.calleds.map((e) => e.value).join(',')
          : '',
      caller: filterState.caller,
      campaignId:
        filterState.campaigns.length > 0
          ? filterState.campaigns.map((e) => e.value).join(',')
          : '',
      duration: filterState.duration,
      from: from_date ? format(from_date, 'yyyy-MM-dd HH:mm') : '',
      queueId:
        filterState.queues.length > 0
          ? filterState.queues.map((e) => e.value).join(',')
          : '',
      to: to_date ? format(to_date, 'yyyy-MM-dd HH:mm') : '',
      acdTimeGt: filterState.acdTimeGt || '',
      acdTimeLt: filterState.acdTimeLt || '',
    }

    if (isRoleAgent()) {
      payload.agentId = user.agent_id
    }

    return exportCallHistory({
      data: payload,
      params: {
        page: filterState.page,
        pageSize: filterState.pageSize,
      },
    })
  }

  const { isPending: isExporting, mutate: onExportClick } = useMutation({
    mutationFn: handleExportCallHistory,
    onSuccess: () => {
      return toast.success(
        'File đang được xử lý. Hãy kiểm tra mục thông báo sau ít phút',
      )
    },
    onError: (error) => throwException(error),
  })

  return (
    <Layout.Body>
      <div>
        <CallHistoryDetailFilterForm
          exportExcel={onExportClick}
          isExporting={isExporting}
          handleTabDateChange={handleTabChange}
        />
        <div className="mt-4">
          <div className="mb-4 mt-8 flex items-center justify-between">
            <CardTitle>Báo cáo lịch sử cuộc gọi</CardTitle>
            <div>
              <Tabs
                value={tabFilterDateDf}
                className="w-full"
                onValueChange={handleTabChange}
              >
                <TabsList>
                  {tabFilterDate.map((tab) => (
                    <TabsTrigger key={tab.value} value={tab.value}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </div>

          <Card className="mx-auto w-full max-w-full">
            <CardContent>
              <CallHistoryDetailTable
                data={dataCallHistory}
                isLoading={isLoadingCallHistory}
                pageSize={10}
              />
              {!isLoadingCallHistory && (
                <CallHistoryDetailTablePagination
                  totalRecords={queryDataCallHistory?.data?.total}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout.Body>
  )
}

const getDataCallHistory = (dataObj) => {
  const {
    queryDataCallHistory,
    queryDataAllQueues,
    queryDataAllAgent,
    campaigns,
  } = dataObj
  if (!queryDataCallHistory) {
    return []
  }

  const datas = queryDataCallHistory?.data?.map((item) => {
    const agents = item.agentId.split(',')

    return {
      ...item,
      callType: callTypeView(item.callType),
      queue: queryDataAllQueues?.find((queue) => queue.QUEUE_ID == item.queueId)
        ?.DESCRIPTION,
      campaign: ['HAPPYCALL', 'ZALO'].includes(item.campaignId)
        ? item.campaignId
        : campaigns.find((e) => e._id == item.campaignId)?.name,
      waitingTime: formatSecondToHour(item.waitingTime),
      acdTime: formatSecondToHour(item.acdTime),
      duration: formatSecondToHour(item.duration),
      status: !!item.connectTime,
      agent: queryDataAllAgent
        ?.filter((agent) => agents.includes(agent.agent_id))
        .map((e) => e.name)
        ?.join(', '),
      displayUrlFile: renderDisplayUrlFile(item.connectTime, item.url),
      ringTime: formatDateTime(item.ringTime),
      connectTime: item.connectTime && formatDateTime(item.connectTime),
      startTime: formatDateTime(item.startTime),
      endTime: formatDateTime(item.endTime),
    }
  })
  return datas
}

const renderDisplayUrlFile = (connectTime, url) => {
  if (!connectTime) return ''
  return (
    <div className="flex items-center gap-2">
      <Dialog>
        <DialogTrigger asChild>
          <Play className="text-sm" />
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <audio controls className="mt-4 w-full">
            <source src={url} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function formatDateTime(input) {
  if (!input) {
    return '-'
  }
  const [date, time] = input.split(' ')
  const [year, month, day] = date.split('-')
  return `${day}/${month}/${year} ${time}`
}
